import type { App as VueApp } from "vue";
import { createI18n } from "vue-i18n";
import { localStg } from "@/utils/storage";
import zhCN from "./langs/zh-cn.json";
import enUS from "./langs/en-us.json";
import esES from "./langs/es-es.json";
import thTH from "./langs/th-th.json";
import viVN from "./langs/vi-vn.json";
import koKR from "./langs/ko-kr.json";
import htHT from "./langs/ht-ht.json";
import type { App } from "@/types/app";
import { allowedLanguages, localeOptions } from "@/config/lang";

const messages = {
  "zh-CN": zhCN,
  "en-US": enUS,
  "es-ES": esES,
  // 泰文
  "th-TH": thTH,
  // 越南文
  "vi-VN": viVN,
  // 韩文
  "ko-KR": koKR,
  // 海地文
  "ht-HT": htHT,
};

// 新增获取默认语言的函数
export function getDefaultLocale(): App.I18n.LangType {
  // 0. 优先检查URL参数中的lang参数
  const urlParams = new URLSearchParams(window.location.search);
  const urlLang = urlParams.get("lang");
  if (urlLang) {
    // 先精确匹配，再短码匹配（如 "en" 匹配 "en-US"）
    const matched = allowedLanguages.find(
      (lang) => lang === urlLang || lang.startsWith(urlLang.split("-")[0])
    );
    if (matched) return matched;
  }

  // 1. 使用本地存储的语言
  const localLang = localStg.get("lang");
  if (localLang && allowedLanguages.includes(localLang as any)) {
    return localLang as App.I18n.LangType;
  }

  // 2. 获取浏览器语言并处理
  const browserLang = navigator.language;
  const matchedLang = allowedLanguages.find(
    (lang) => lang === browserLang || lang.startsWith(browserLang.split("-")[0])
  );

  // 3. 返回匹配到的语言或默认英语
  return matchedLang || "en-US";
}

const i18n = createI18n({
  locale: getDefaultLocale(),
  fallbackLocale: "en-US",
  messages,
  legacy: false,
  globalInjection: true,
  silentTranslationWarn: false,
  missingWarn: false,
  fallbackWarn: false,
});

/**
 * 设置国际化
 * @param app 应用实例
 */
export function setupI18n(app: VueApp) {
  app.use(i18n);
}

export const $t = i18n.global.t;

export function setLocale(locale: App.I18n.LangType) {
  localStg.set("lang", locale); // 存储设置
  i18n.global.locale.value = locale;

  // 处理URL中的语言参数
  const url = new URL(window.location.href);
  if (url.searchParams.has("lang")) {
    url.searchParams.delete("lang");
    window.history.replaceState({}, "", url.toString());
  }
}

export { allowedLanguages, localeOptions };
